/* eslint-disable @typescript-eslint/no-unused-vars */
import ReactMarkdown from 'react-markdown'
import remarkGfm from 'remark-gfm'
import styled from 'styled-components'
import {
  Blockquote,
  BodyLarge,
  RichTextLink as AnchorTag,
  Title1,
  Title2,
  Title3,
  Title4,
  Title5,
  Title6,
} from '../styles/Typography'
import { theme } from '../../theme/theme'
import { Link } from 'react-router-dom'
import { isInternalLink, stripPortalDomain } from '../../utils/links'

const StyledTitle1 = styled(Title1)`
  margin-top: ${theme.spacing.small}px;
  margin-bottom: ${theme.spacing.xsmall}px;
  font-size: ${theme.fontSize.xxlarge}px;
  line-height: 46px;
`

const StyledTitle2 = styled(Title2)`
  margin-top: ${theme.spacing.small}px;
  margin-bottom: ${theme.spacing.xsmall}px;
  font-size: ${theme.fontSize.xlarge}px;
  line-height: 38px;
`
const StyledTitle3 = styled(Title3)`
  margin-top: ${theme.spacing.mediumLarge}px;
  margin-bottom: ${theme.spacing.xsmall}px;
  font-size: ${theme.fontSize.large}px;
  line-height: 32px;
`
const StyledTitle4 = styled(Title4)<{ $healthCard?: boolean }>`
  margin-top: ${({ $healthCard }) => ($healthCard ? '0px' : `${theme.spacing.mediumLarge}px`)};
  margin-bottom: ${theme.spacing.xsmall}px;
  font-size: ${theme.fontSize.mediumLarge}px;
  line-height: 30px;
`
const StyledTitle5 = styled(Title5)<{ $healthCard?: boolean }>`
  margin-top: ${({ $healthCard }) => ($healthCard ? '0px' : `${theme.spacing.mediumLarge}px`)};
  margin-bottom: ${theme.spacing.xsmall}px;
  font-size: ${theme.fontSize.medium}px;
  line-height: 28px;
`
const StyledTitle6 = styled(Title6)<{ $healthCard?: boolean }>`
  margin-top: ${({ $healthCard }) => ($healthCard ? '0px' : `${theme.spacing.small}px`)};
  margin-bottom: ${theme.spacing.xsmall}px;
  font-size: ${theme.fontSize.default}px;
  line-height: 26px;
`

export const StyledBold = styled.span`
  font-weight: 500;
`

const StyledBody = styled(BodyLarge)<{ $healthCard?: boolean }>`
  margin-bottom: ${theme.spacing.medium}px;

  ${({ $healthCard }) =>
    $healthCard &&
    `
    font-size: ${theme.fontSize.small}px;
    line-height: 22px;

    &:has(${StyledBold}) {
      margin-bottom: ${theme.spacing.tiny}px;
    }

    &:has(+ ul),
    &:has(+ ol) {
      margin-bottom: 0;
    }
  `}
`

const StyledList = styled.ul<{ $healthCard?: boolean }>`
  margin-top: 0;
  margin-bottom: ${theme.spacing.medium}px;
  padding-left: 24px;
  list-style-type: disc;
  ${({ $healthCard }) =>
    $healthCard &&
    `
    font-size: ${theme.fontSize.small}px;
    line-height: 22px;
  `}
`

const StyledOrderedList = styled.ol<{ $healthCard?: boolean }>`
  margin-top: 0;
  margin-bottom: ${theme.spacing.medium}px;
  padding-left: 24px;
  ${({ $healthCard }) =>
    $healthCard &&
    `
    font-size: ${theme.fontSize.small}px;
    line-height: 22px;
  `}
`

export const StyledReactLink = styled(Link)`
  color: ${theme.color.plumMid};
  text-decoration: underline;
  text-decoration-color: ${theme.color.plumMid};
`

export const Divider = styled.hr`
  border-bottom: 0.5px solid ${theme.color.grey1};
  width: 100%;
  margin: ${theme.spacing.xlarge}px 0px;
`

const TableScroll = styled.div`
  overflow-x: auto;
  margin-bottom: ${theme.spacing.medium}px;
`

const StyledTable = styled.table`
  border-collapse: collapse;
  width: 100%;
  min-width: 480px;
  font-size: ${theme.fontSize.small}px;
  line-height: 20px;
`

const StyledTableHeadCell = styled.th`
  text-align: left;
  vertical-align: top;
  font-weight: 500;
  padding: ${theme.spacing.xsmall}px 10px;
  background: ${theme.color.beigeLight};
  border-bottom: 1px solid ${theme.color.grey2};
`

const StyledTableCell = styled.td`
  text-align: left;
  vertical-align: top;
  padding: ${theme.spacing.xsmall}px 10px;
  border-bottom: 1px solid ${theme.color.grey2};
`

export const MarkdownToReact = ({
  markdown,
  healthCard = false,
  gfm = false,
}: {
  markdown: string
  healthCard?: boolean
  gfm?: boolean
}) => {
  return (
    <ReactMarkdown
      remarkPlugins={gfm ? [remarkGfm] : []}
      components={{
        h1: ({ node, ref, ...props }) => <StyledTitle1 {...props} />,
        h2: ({ node, ref, ...props }) => <StyledTitle2 {...props} />,
        h3: ({ node, ref, ...props }) => <StyledTitle3 {...props} />,
        h4: ({ node, ref, ...props }) => <StyledTitle4 $healthCard={healthCard} {...props} />,
        h5: ({ node, ref, ...props }) => <StyledTitle5 $healthCard={healthCard} {...props} />,
        h6: ({ node, ref, ...props }) => <StyledTitle6 $healthCard={healthCard} {...props} />,
        p: ({ node, ref, ...props }) => <StyledBody $healthCard={healthCard} {...props} />,
        strong: ({ node, ref, ...props }) => <StyledBold {...props} />,
        li: ({ node, ref, ...props }) => <li {...props} />,
        ul: ({ node, ref, ...props }) => <StyledList $healthCard={healthCard} {...props} />,
        ol: ({ node, ref, ...props }) => <StyledOrderedList $healthCard={healthCard} {...props} />,
        a: ({ href, children }) => {
          const strippedHref = stripPortalDomain(href ?? '')
          return isInternalLink(strippedHref) ? (
            <StyledReactLink to={strippedHref}>{children}</StyledReactLink>
          ) : (
            <AnchorTag href={href} target="_blank" rel="noreferrer">
              {children}
            </AnchorTag>
          )
        },
        em: ({ node, ref, ...props }) => <em {...props} />,
        blockquote: (props) => <Blockquote {...props} />,
        br: (props) => <br {...props} />,
        hr: (props) => <Divider {...props} />,
        table: ({ node, ref, ...props }) => (
          <TableScroll>
            <StyledTable {...props} />
          </TableScroll>
        ),
        th: ({ node, ref, ...props }) => <StyledTableHeadCell {...props} />,
        td: ({ node, ref, ...props }) => <StyledTableCell {...props} />,
      }}
    >
      {markdown}
    </ReactMarkdown>
  )
}
